import { useState, useEffect, useMemo, useRef } from "react";
import { useAdminUsers, useAdminUserUsage, useAdminUpdateUser, useAdminDeleteUser, useAdminRoles, useAdminMe, useAdminPlans, useAdminUpdateUserSubscription } from "../hooks/useApi";
import { formatNumber, toPersianDigits, normalizeDecimal, decimalToPlainString } from "../utils/format";
import toast from "react-hot-toast";
import AdminUserDrawer from "../components/AdminUserDrawer";
import "../admin-theme.css";

const STATUS_OPTIONS = ["active", "inactive", "banned"];

const BACKFILL_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "— (بدون بکفیل)" },
  { value: "pending", label: "pending — در انتظار" },
  { value: "done", label: "done — تکمیل شده" },
  { value: "failed", label: "failed — ناموفق" },
];

const OPENROUTER_KEY_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "unprovisioned", label: "unprovisioned — پیش از مهاجرت" },
  { value: "provisioning", label: "provisioning — در حال صدور" },
  { value: "ready", label: "ready — آماده" },
  { value: "provisioning_failed", label: "provisioning_failed — ناموفق" },
  { value: "unknown", label: "unknown — نامشخص" },
  { value: "inconsistent", label: "inconsistent — ناسازگار (نیاز به بررسی)" },
];

const HAS_HASH_OPTIONS: { value: string; label: string }[] = [
  { value: "true", label: "دارد" },
  { value: "false", label: "ندارد" },
];

interface EditForm {
  user_id: number;
  name: string;
  status: string;
  is_admin: boolean;
  wallet_balance: string;
  role_ids: number[];
  sub_plan_id: number | null;
  sub_start: string;
  sub_end: string;
  initial_sub_plan_id: number | null;
  initial_sub_start: string;
  initial_sub_end: string;
  initial_sub_is_free: boolean;
  fixed_sub_plan_id: number | null;
  fixed_sub_start: string;
  fixed_sub_end: string;
  fixed_codecraft_key_number: number | null;
  initial_fixed_sub_plan_id: number | null;
  initial_fixed_sub_start: string;
  initial_fixed_sub_end: string;
  backfill_status: string | null;
  openrouter_key_status: string;
  has_hash: boolean;
  initial_backfill_status: string | null;
  initial_openrouter_key_status: string;
  initial_has_hash: boolean;
}

export default function AdminUsers() {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [filterBackfill, setFilterBackfill] = useState<string>("");
  const [filterORStatus, setFilterORStatus] = useState<string>("");
  const [filterHasHash, setFilterHasHash] = useState<string>("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      plan_id: selectedPlan ? Number(selectedPlan) : undefined,
      sort: sortOrder,
      backfill_status: filterBackfill || undefined,
      openrouter_key_status: filterORStatus || undefined,
      has_hash: filterHasHash || undefined,
      limit: 50,
      offset: 0,
    }),
    [debouncedSearch, selectedPlan, sortOrder, filterBackfill, filterORStatus, filterHasHash]
  );

  const { data: users, isLoading } = useAdminUsers(params);
  const { data: roles } = useAdminRoles();
  const { data: plans } = useAdminPlans();
  const { data: me } = useAdminMe();
  const updateUser = useAdminUpdateUser();
  const updateSubscription = useAdminUpdateUserSubscription();
  const deleteUserMutation = useAdminDeleteUser();

  const canManage = Boolean(me?.is_admin) || Boolean(me?.permissions?.includes("users.manage"));
  const canManageSubscriptions =
    Boolean(me?.is_admin) || Boolean(me?.permissions?.includes("subscriptions.manage"));
  const [editUser, setEditUser] = useState<EditForm | null>(null);
  const [profileUser, setProfileUser] = useState<Record<string, unknown> | null>(null);
  const [blockTarget, setBlockTarget] = useState<Record<string, unknown> | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Record<string, unknown> | null>(null);

  const { data: userUsage } = useAdminUserUsage(editUser?.user_id ?? null);
  const overlayMouseDown = useRef(false);

  const planOptions = (plans as Record<string, unknown>[] | undefined) || [];
  const paygPlanOptions = planOptions.filter((p) => p.plan_kind !== "fixed");
  const fixedPlanOptions = planOptions.filter((p) => p.plan_kind === "fixed");

  const selectedEditPlan = planOptions.find((p) => p.plan_id === editUser?.sub_plan_id);
  const selectedIsFree =
    (selectedEditPlan ? Number(selectedEditPlan.monthly_price) === 0 : editUser?.initial_sub_is_free) || false;

  const fixedSubChanged = Boolean(
    editUser &&
      (editUser.fixed_sub_plan_id !== editUser.initial_fixed_sub_plan_id ||
        editUser.fixed_sub_start !== editUser.initial_fixed_sub_start ||
        editUser.fixed_sub_end !== editUser.initial_fixed_sub_end)
  );

  const subChanged = Boolean(
    editUser &&
      (editUser.sub_plan_id !== editUser.initial_sub_plan_id ||
        editUser.sub_start !== editUser.initial_sub_start ||
        editUser.sub_end !== editUser.initial_sub_end)
  );

  const hasActiveFilters = Boolean(debouncedSearch || selectedPlan || sortOrder !== "newest" || filterBackfill || filterORStatus || filterHasHash);

  const clearFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setSelectedPlan("");
    setSortOrder("newest");
    setFilterBackfill("");
    setFilterORStatus("");
    setFilterHasHash("");
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case "active": return <span className="admin-badge admin-badge-success">فعال</span>;
      case "inactive": return <span className="admin-badge admin-badge-warning">غیرفعال</span>;
      case "banned": return <span className="admin-badge admin-badge-error">مسدود</span>;
      case "deleted": return <span className="admin-badge admin-badge-info">حذف‌شده</span>;
      default: return <span className="admin-badge admin-badge-info">{status}</span>;
    }
  };

  const adminBadge = (isAdmin: boolean) => {
    return isAdmin ? (
      <span className="admin-badge admin-badge-info">مدیر</span>
    ) : (
      <span className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>—</span>
    );
  };

  const roleBadges = (roleNames: string[] | undefined) => {
    if (!roleNames || roleNames.length === 0) return null;
    return roleNames.map((name) => (
      <span
        key={name}
        className="admin-badge"
        style={{ background: "var(--admin-primary-light)", color: "var(--admin-primary-dark)", fontSize: 11 }}
      >
        {name}
      </span>
    ));
  };

  const openEdit = (user: Record<string, unknown>) => {
    const sub = (user.subscription as Record<string, unknown>) || null;
    const subFixed = (user.subscription_fixed as Record<string, unknown>) || null;
    const bf = (user.backfill_status as string | null) ?? null;
    const okr = (user.openrouter_key_status as string) || "unprovisioned";
    const hh = Boolean(user.has_hash);
    setEditUser({
      user_id: user.id as number,
      name: (user.name as string) || "",
      status: (user.status as string) || "active",
      is_admin: Boolean(user.is_admin),
      wallet_balance: decimalToPlainString(user.wallet_balance as string | number | null),
      role_ids: ((user.roles as { role_id: number }[]) || []).map((r) => r.role_id),
      sub_plan_id: sub ? (sub.plan_id as number) : null,
      sub_start: sub ? ((sub.start_date as string) || "").slice(0, 10) : "",
      sub_end: sub ? ((sub.end_date as string) || "").slice(0, 10) : "",
      initial_sub_plan_id: sub ? (sub.plan_id as number) : null,
      initial_sub_start: sub ? ((sub.start_date as string) || "").slice(0, 10) : "",
      initial_sub_end: sub ? ((sub.end_date as string) || "").slice(0, 10) : "",
      initial_sub_is_free: sub ? Boolean(sub.is_free) : true,
      fixed_sub_plan_id: subFixed ? (subFixed.plan_id as number) : null,
      fixed_sub_start: subFixed ? ((subFixed.start_date as string) || "").slice(0, 10) : "",
      fixed_sub_end: subFixed ? ((subFixed.end_date as string) || "").slice(0, 10) : "",
      fixed_codecraft_key_number: (subFixed?.codecraft_key_number as number | null) ?? null,
      initial_fixed_sub_plan_id: subFixed ? (subFixed.plan_id as number) : null,
      initial_fixed_sub_start: subFixed ? ((subFixed.start_date as string) || "").slice(0, 10) : "",
      initial_fixed_sub_end: subFixed ? ((subFixed.end_date as string) || "").slice(0, 10) : "",
      backfill_status: bf,
      openrouter_key_status: okr,
      has_hash: hh,
      initial_backfill_status: bf,
      initial_openrouter_key_status: okr,
      initial_has_hash: hh,
    });
  };

  const handleSave = () => {
    if (!editUser) return;
    const data: Record<string, unknown> = {
      name: editUser.name,
      status: editUser.status,
      is_admin: editUser.is_admin,
      role_ids: editUser.role_ids,
    };
    if (editUser.wallet_balance !== "") {
      data.wallet_balance = Number(editUser.wallet_balance);
    }
    // Provisioning / backfill editable fields — only send if changed
    if (editUser.backfill_status !== editUser.initial_backfill_status) {
      // send null explicitly when cleared (empty string -> null validated on backend)
      data.backfill_status = editUser.backfill_status;
    }
    if (editUser.openrouter_key_status !== editUser.initial_openrouter_key_status) {
      data.openrouter_key_status = editUser.openrouter_key_status;
    }
    if (editUser.has_hash !== editUser.initial_has_hash) {
      data.has_hash = editUser.has_hash;
    }

    if (canManageSubscriptions && subChanged && editUser.sub_plan_id != null) {
      if (editUser.sub_start && editUser.sub_end && new Date(editUser.sub_end) <= new Date(editUser.sub_start)) {
        toast.error("تاریخ پایان باید بعد از تاریخ شروع باشد");
        return;
      }
      const subData: Record<string, unknown> = { plan_id: editUser.sub_plan_id };
      if (editUser.sub_start) subData.start_date = editUser.sub_start;
      if (editUser.sub_end) subData.end_date = editUser.sub_end;
      updateSubscription.mutate({ id: String(editUser.user_id), data: subData });
    }

    if (canManageSubscriptions && fixedSubChanged) {
      if (editUser.fixed_sub_plan_id != null && editUser.fixed_sub_start && editUser.fixed_sub_end && new Date(editUser.fixed_sub_end) <= new Date(editUser.fixed_sub_start)) {
        toast.error("تاریخ پایان تعرفه ثابت باید بعد از تاریخ شروع باشد");
        return;
      }
      const fixedSubData: Record<string, unknown> = { plan_id: editUser.fixed_sub_plan_id };
      if (editUser.fixed_sub_plan_id != null && editUser.fixed_sub_start) fixedSubData.start_date = editUser.fixed_sub_start;
      if (editUser.fixed_sub_plan_id != null && editUser.fixed_sub_end) fixedSubData.end_date = editUser.fixed_sub_end;
      updateSubscription.mutate({ id: String(editUser.user_id), data: fixedSubData });
    }

    updateUser.mutate({ id: String(editUser.user_id), data });
    setEditUser(null);
  };

  const toggleBlock = (user: Record<string, unknown>) => {
    setBlockTarget(user);
  };

  const confirmBlock = () => {
    if (!blockTarget) return;
    const targetStatus = blockTarget.status === "banned" ? "active" : "banned";
    updateUser.mutate({ id: String(blockTarget.id), data: { status: targetStatus } });
    setBlockTarget(null);
  };

  const toggleDelete = (user: Record<string, unknown>) => {
    setDeleteTarget(user);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteUserMutation.mutate(String(deleteTarget.id), {
      onSuccess: () => setDeleteTarget(null),
    });
  };

  const toggleRole = (roleId: number) => {
    if (!editUser) return;
    setEditUser((f) =>
      f && {
        ...f,
        role_ids: f.role_ids.includes(roleId)
          ? f.role_ids.filter((id) => id !== roleId)
          : [...f.role_ids, roleId],
      }
    );
  };

  const displayName = (user: Record<string, unknown>) => {
    const name = (user.name as string) || "";
    const family = (user.family_name as string) || "";
    const full = [name, family].filter(Boolean).join(" ").trim();
    return full || "—";
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>کاربران</h1>
          <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>مدیریت کاربران سیستم</p>
        </div>
      </div>

      {/* Filters Toolbar */}
      <div className="admin-card">
        <div className="p-4 flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2"
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="var(--admin-text-tertiary)" strokeWidth="2" strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="جستجو بر اساس ایمیل، نام، نام خانوادگی یا شماره موبایل..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="admin-input"
              style={{ paddingRight: 40 }}
            />
          </div>

          {/* Plan Filter */}
          <select
            value={selectedPlan}
            onChange={(e) => setSelectedPlan(e.target.value)}
            className="admin-input"
            style={{ width: 190 }}
          >
            <option value="">همه پلن‌ها</option>
            {planOptions.map((plan) => {
              const isFree = Number(plan.monthly_price) === 0;
              return (
                <option key={plan.plan_id as number} value={String(plan.plan_id)}>
                  {plan.name as string}
                  {isFree ? " (رایگان)" : ""}
                </option>
              );
            })}
          </select>

          {/* Sort */}
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "newest" | "oldest")}
            className="admin-input"
            style={{ width: 150 }}
          >
            <option value="newest">جدیدترین</option>
            <option value="oldest">قدیمی‌ترین</option>
          </select>

          {/* Backfill Filter */}
          <select
            value={filterBackfill}
            onChange={(e) => setFilterBackfill(e.target.value)}
            className="admin-input"
            style={{ width: 170 }}
            title="فیلتر بر اساس بکفیل"
          >
            <option value="">همه بکفیل‌ها</option>
            <option value="__null">بدون بکفیل (—)</option>
            <option value="pending">pending</option>
            <option value="done">done</option>
            <option value="failed">failed</option>
          </select>

          {/* OpenRouter Key Status Filter */}
          <select
            value={filterORStatus}
            onChange={(e) => setFilterORStatus(e.target.value)}
            className="admin-input"
            style={{ width: 200 }}
            title="فیلتر بر اساس وضعیت کلید"
          >
            <option value="">همه وضعیت‌های کلید</option>
            <option value="unprovisioned">unprovisioned</option>
            <option value="provisioning">provisioning</option>
            <option value="ready">ready</option>
            <option value="provisioning_failed">provisioning_failed</option>
            <option value="unknown">unknown</option>
            <option value="inconsistent">inconsistent</option>
          </select>

          {/* Has Hash Filter */}
          <select
            value={filterHasHash}
            onChange={(e) => setFilterHasHash(e.target.value)}
            className="admin-input"
            style={{ width: 150 }}
            title="فیلتر بر اساس هش"
          >
            <option value="">همه هش‌ها</option>
            <option value="true">هش دارد</option>
            <option value="false">هش ندارد</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="admin-btn admin-btn-secondary admin-btn-sm"
              title="پاک کردن فیلترها"
            >
              پاک کردن فیلتر
            </button>
          )}

          <span className="text-xs mr-auto" style={{ color: "var(--admin-text-tertiary)" }}>
            {users ? `${(users as unknown[]).length.toLocaleString("fa-IR")} کاربر` : ""}
          </span>
        </div>
      </div>

      {/* Users Table */}
      <div className="admin-card overflow-hidden">
        <div className="admin-card-header">
          <h2 className="admin-card-title">لیست کاربران</h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : !users?.length ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3 opacity-50" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
            <p className="text-sm">هیچ کاربری یافت نشد</p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="admin-btn admin-btn-secondary admin-btn-sm mt-3">
                پاک کردن فیلترها
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>نام</th>
                  <th>ایمیل</th>
                  <th>شماره موبایل</th>
                  <th>وضعیت</th>
                  <th>نقش</th>
                  <th>دسترسی</th>
                  <th>بکفیل</th>
                  <th>وضعیت کلید</th>
                  <th>هش</th>
                  <th>اشتراک</th>
                  <th>تعرفه ثابت</th>
                  <th>موجودی</th>
                  <th>تاریخ ثبت‌نام</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {(users as Record<string, unknown>[]).map((user) => {
                  const userRoles = ((user.roles as { name: string }[]) || []).map((r) => r.name);
                  const bfStatus = user.backfill_status as string | null;
                  const orStatus = user.openrouter_key_status as string | null;
                  const hasHash = Boolean(user.has_hash);
                  const backfillBadge = (s: string | null) => {
                    if (!s) return <span className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
                    if (s === "done") return <span className="admin-badge admin-badge-success">done</span>;
                    if (s === "pending") return <span className="admin-badge admin-badge-warning">pending</span>;
                    if (s === "failed") return <span className="admin-badge admin-badge-error">failed</span>;
                    return <span className="admin-badge admin-badge-info">{s}</span>;
                  };
                  const orBadge = (s: string | null) => {
                    if (!s) return <span className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
                    const map: Record<string, string> = {
                      ready: "admin-badge-success",
                      unprovisioned: "admin-badge-info",
                      provisioning: "admin-badge-info",
                      provisioning_failed: "admin-badge-error",
                      unknown: "admin-badge-warning",
                      inconsistent: "admin-badge-error",
                    };
                    const cls = map[s] || "admin-badge-info";
                    return <span className={`admin-badge ${cls}`}>{s}</span>;
                  };
                  return (
                    <tr
                      key={user.id as string}
                      onClick={() => setProfileUser(user)}
                      className="cursor-pointer"
                      title="مشاهده پروفایل کاربر"
                    >
                      <td>
                        <span className="text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>
                          {displayName(user)}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm ltr text-left block" style={{ color: "var(--admin-text-secondary)" }}>
                          {(user.email as string) || "—"}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm ltr text-left block" style={{ color: "var(--admin-text-secondary)" }}>
                          {(user.phone as string) || "—"}
                        </span>
                      </td>
                      <td>{statusBadge(user.status as string)}</td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {userRoles.length ? roleBadges(userRoles) : <span className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>—</span>}
                        </div>
                      </td>
                      <td>{adminBadge(Boolean(user.is_admin))}</td>
                      <td>{backfillBadge(bfStatus)}</td>
                      <td>{orBadge(orStatus)}</td>
                      <td>
                        {hasHash ? (
                          <span className="admin-badge admin-badge-success">دارد</span>
                        ) : (
                          <span className="admin-badge" style={{ background: "var(--admin-bg-hover)", color: "var(--admin-text-tertiary)" }}>ندارد</span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const sub = (user.subscription as Record<string, unknown>) || null;
                          if (!sub) {
                            return <span className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
                          }
                          return (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                              style={{
                                background: sub.is_free ? "var(--admin-bg-hover)" : "var(--admin-primary-light)",
                                color: sub.is_free ? "var(--admin-text-secondary)" : "var(--admin-primary-dark)",
                              }}
                            >
                              {sub.plan_name as string}
                              {Boolean(sub.is_free) && <span>(رایگان)</span>}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        {(() => {
                          const fixedSub = (user.subscription_fixed as Record<string, unknown>) || null;
                          if (!fixedSub) {
                            return <span className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
                          }
                          return (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: "var(--admin-info-bg)", color: "var(--admin-primary-dark)" }}
                            >
                              {fixedSub.plan_name as string}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        <span className="font-number text-xs" style={{ color: "var(--admin-text-primary)" }}>
                          {formatNumber((user.wallet_balance as number) || 0)}
                        </span>
                      </td>
                      <td>
                        <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                          {new Date(user.created_at as string).toLocaleDateString("fa-IR")}
                        </span>
                      </td>
                      <td>
                        {user.status === "deleted" ? (
                          <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>حذف‌شده</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              className="admin-btn admin-btn-secondary admin-btn-sm"
                              disabled={!canManage}
                              style={{ opacity: canManage ? 1 : 0.4, cursor: canManage ? "pointer" : "not-allowed" }}
                              onClick={(e) => { e.stopPropagation(); openEdit(user); }}
                            >
                              ویرایش
                            </button>
                            <button
                              className="admin-btn admin-btn-sm"
                              disabled={!canManage}
                              style={{
                                background: "var(--admin-error-bg)",
                                color: "var(--admin-error)",
                                border: "none",
                                opacity: canManage ? 1 : 0.4,
                                cursor: canManage ? "pointer" : "not-allowed",
                              }}
                              onClick={(e) => { e.stopPropagation(); toggleBlock(user); }}
                            >
                              {user.status === "banned" ? "رفع مسدودی" : "مسدود"}
                            </button>
                            <button
                              className="admin-btn admin-btn-sm"
                              disabled={!canManage}
                              style={{
                                background: "var(--admin-error-bg)",
                                color: "var(--admin-error)",
                                border: "1px solid var(--admin-error)",
                                opacity: canManage ? 1 : 0.4,
                                cursor: canManage ? "pointer" : "not-allowed",
                              }}
                              onClick={(e) => { e.stopPropagation(); toggleDelete(user); }}
                            >
                              حذف
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editUser && (
        <div
          className="admin-overlay"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setEditUser(null); overlayMouseDown.current = false; }}
        >
          <div
            className="admin-glass admin-modal"
            style={{ maxWidth: 520 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                ویرایش کاربر
              </h2>
              <button
                onClick={() => setEditUser(null)}
                className="p-1 rounded-lg transition-colors"
                style={{ color: "var(--admin-text-tertiary)" }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  نام
                </label>
                <input
                  type="text"
                  value={editUser.name}
                  onChange={(e) => setEditUser((f) => f && { ...f, name: e.target.value })}
                  className="admin-input"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  وضعیت
                </label>
                <select
                  value={editUser.status}
                  onChange={(e) => setEditUser((f) => f && { ...f, status: e.target.value })}
                  className="admin-input"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s === "active" ? "فعال" : s === "inactive" ? "غیرفعال" : "مسدود"}
                    </option>
                  ))}
                </select>
              </div>

              {/* Is admin */}
              <label className="flex items-center justify-between rounded-xl px-4 py-3 cursor-pointer" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                <span className="text-sm font-medium" style={{ color: "var(--admin-text-primary)" }}>مدیر سیستم</span>
                <button
                  type="button"
                  className={`admin-toggle${editUser.is_admin ? " on" : ""}`}
                  onClick={() => setEditUser((f) => f && { ...f, is_admin: !f.is_admin })}
                />
              </label>

              {/* Roles */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "var(--admin-text-secondary)" }}>
                  نقش‌ها
                </label>
                {!roles?.length ? (
                  <p className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                    ابتدا از بخش نقش‌ها، نقش‌ها را تعریف کنید
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl p-3" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                    {(roles as Record<string, unknown>[]).map((role) => (
                      <label
                        key={role.role_id as string}
                        className="flex items-center gap-2.5 px-1 py-1 rounded-lg cursor-pointer text-sm"
                        style={{ color: "var(--admin-text-primary)" }}
                      >
                        <input
                          type="checkbox"
                          checked={editUser.role_ids.includes(role.role_id as number)}
                          onChange={() => toggleRole(role.role_id as number)}
                          style={{ accentColor: "var(--admin-primary)" }}
                        />
                        {role.display_name as string}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Subscription — pay-as-you-go (OpenRouter gateway) */}
              {canManageSubscriptions && (
                <div className="rounded-xl p-3 space-y-3" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                  <p className="text-xs font-semibold" style={{ color: "var(--admin-text-secondary)" }}>
                    اشتراک پلن مصرفی (API عمومی — کلیدهای API /v1)
                  </p>
                  <select
                    value={editUser.sub_plan_id == null ? "" : String(editUser.sub_plan_id)}
                    onChange={(e) => {
                      const planId = e.target.value === "" ? null : Number(e.target.value);
                      const plan = paygPlanOptions.find((p) => p.plan_id === planId);
                      const isFreePlan = plan ? Number(plan.monthly_price) === 0 : false;
                      setEditUser((f) =>
                        f && {
                          ...f,
                          sub_plan_id: planId,
                          sub_end: isFreePlan ? "" : f.sub_end,
                        }
                      );
                    }}
                    className="admin-input"
                  >
                    <option value="">انتخاب پلن...</option>
                    {paygPlanOptions.map((plan) => {
                      const isFreePlan = Number(plan.monthly_price) === 0;
                      return (
                        <option key={plan.plan_id as number} value={String(plan.plan_id)} disabled={!plan.is_active}>
                          {plan.name as string}
                          {isFreePlan ? " (رایگان)" : ` (${formatNumber(Number(plan.monthly_price))})`}
                          {plan.is_public ? "" : " (خصوصی)"}
                        </option>
                      );
                    })}
                  </select>

                  {selectedIsFree ? (
                    <p className="text-xs mt-1.5" style={{ color: "var(--admin-text-tertiary)" }}>
                      پلن رایگان تاریخ شروع و پایان ندارد
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                          تاریخ شروع
                        </label>
                        <input
                          type="date"
                          value={editUser.sub_start}
                          onChange={(e) => setEditUser((f) => f && { ...f, sub_start: e.target.value })}
                          className="admin-input"
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                          تاریخ پایان
                        </label>
                        <input
                          type="date"
                          value={editUser.sub_end}
                          onChange={(e) => setEditUser((f) => f && { ...f, sub_end: e.target.value })}
                          className="admin-input"
                          dir="ltr"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Subscription — fixed-cost (squat monthly quota) */}
              {canManageSubscriptions && (
                <div className="rounded-xl p-3 space-y-3" style={{ background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.35)" }}>
                  <p className="text-xs font-semibold" style={{ color: "var(--admin-text-secondary)" }}>
                    اشتراک تعرفه ثابت (سقف ماهانه)
                  </p>
                  {editUser.fixed_codecraft_key_number != null && (
                    <div className="text-xs" style={{ color: "var(--admin-text-secondary)" }} dir="ltr">
                      CODECRAFT_API_KEY{editUser.fixed_codecraft_key_number}
                    </div>
                  )}
                  <select
                    value={editUser.fixed_sub_plan_id == null ? "" : String(editUser.fixed_sub_plan_id)}
                    onChange={(e) =>
                      setEditUser((f) => f && { ...f, fixed_sub_plan_id: e.target.value === "" ? null : Number(e.target.value) })
                    }
                    className="admin-input"
                  >
                    <option value="">بدون پلن ثابت</option>
                    {fixedPlanOptions.map((plan) => (
                      <option key={plan.plan_id as number} value={String(plan.plan_id)} disabled={!plan.is_active}>
                        {plan.name as string} ({formatNumber(Number(plan.monthly_token_limit))} توکن/ماه)
                      </option>
                    ))}
                  </select>

                  {editUser.fixed_sub_plan_id != null && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                          تاریخ شروع
                        </label>
                        <input
                          type="date"
                          value={editUser.fixed_sub_start}
                          onChange={(e) => setEditUser((f) => f && { ...f, fixed_sub_start: e.target.value })}
                          className="admin-input"
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                          تاریخ پایان
                        </label>
                        <input
                          type="date"
                          value={editUser.fixed_sub_end}
                          onChange={(e) => setEditUser((f) => f && { ...f, fixed_sub_end: e.target.value })}
                          className="admin-input"
                          dir="ltr"
                        />
                      </div>
                    </div>
                  )}
                  <p className="text-xs mt-1.5" style={{ color: "var(--admin-text-tertiary)" }}>
                    پلن ثابت مستقل از پلن مصرفی است؛ هر دو می‌توانند هم‌زمان فعال باشند.
                  </p>
                </div>
              )}

              {/* Usage — user's PAYG API consumption (no fixed/codecraft usage) */}
              <div className="rounded-xl p-3 space-y-3" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                <p className="text-xs font-semibold" style={{ color: "var(--admin-text-secondary)" }}>
                  مصرف API کاربر (فقط پلن مصرفی /v1)
                </p>
                {userUsage ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>کل درخواست‌ها</span>
                      <span className="text-xs font-semibold font-number" style={{ color: "var(--admin-text-primary)" }}>
                        {toPersianDigits(formatNumber((userUsage as { total_requests?: unknown }).total_requests as number))}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>کل توکن‌ها</span>
                      <span className="text-xs font-semibold font-number" style={{ color: "var(--admin-text-primary)" }}>
                        {toPersianDigits(formatNumber((userUsage as { total_tokens?: unknown }).total_tokens as number))}
                      </span>
                    </div>
                    {((userUsage as Record<string, unknown>).top_models as Record<string, unknown>[] | undefined)?.length ? (
                      <div>
                        <p className="text-[11px] mb-1.5" style={{ color: "var(--admin-text-tertiary)" }}>مدل‌های پرارجسته</p>
                        <div className="flex flex-wrap gap-1.5 text-[11px]">
                          {((userUsage as Record<string, unknown>).top_models as Record<string, unknown>[]).map((m) => (
                            <span
                              key={(m.model as string) || "?"}
                              className="px-2 py-0.5 rounded-md"
                              style={{ background: "var(--admin-bg-hover)", color: "var(--admin-text-secondary)" }}
                              dir="ltr"
                            >
                              {m.model as string} · {formatNumber(m.requests as number)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>
                        هنوز درخواست API ثبت نشده است
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>در حال دریافت...</p>
                )}
              </div>

              {/* Provisioning — backfill_status / openrouter_key_status / has_hash */}
              <div className="rounded-xl p-3 space-y-3" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                <p className="text-xs font-semibold" style={{ color: "var(--admin-text-secondary)" }}>تنظیمات پراوژنینگ (Celery)</p>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    backfill_status
                  </label>
                  <select
                    value={editUser.backfill_status ?? ""}
                    onChange={(e) => setEditUser((f) => f && { ...f, backfill_status: e.target.value === "" ? null : e.target.value })}
                    className="admin-input"
                  >
                    {BACKFILL_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <p className="text-xs mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
                    گزینه‌های تسک backfill: pending / done / failed — خالی = کاربر جدید (غیر تارگت)
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    openrouter_key_status
                  </label>
                  <select
                    value={editUser.openrouter_key_status}
                    onChange={(e) => setEditUser((f) => f && { ...f, openrouter_key_status: e.target.value })}
                    className="admin-input"
                  >
                    {OPENROUTER_KEY_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <p className="text-xs mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
                    چرخه پراوژنینگ celery: unprovisioned → provisioning → ready / provisioning_failed / unknown / inconsistent
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    has_hash (openrouter_key_hash)
                  </label>
                  <select
                    value={editUser.has_hash ? "true" : "false"}
                    onChange={(e) => setEditUser((f) => f && { ...f, has_hash: e.target.value === "true" })}
                    className="admin-input"
                  >
                    {HAS_HASH_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <p className="text-xs mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
                    ندارد = حذف هش و سکرت (بازگشت به unprovisioned) — دارد را نمی‌توان بدون هش ساخت
                  </p>
                </div>
              </div>

              {/* Wallet balance */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  موجودی کیف پول (ریال)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={toPersianDigits(editUser.wallet_balance)}
                  onChange={(e) => setEditUser((f) => f && { ...f, wallet_balance: normalizeDecimal(e.target.value) })}
                  className="admin-input font-number"
                  dir="ltr"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setEditUser(null)}
                  className="admin-btn admin-btn-secondary flex-1"
                >
                  انصراف
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateUser.isPending}
                  className="admin-btn admin-btn-primary flex-1"
                >
                  {updateUser.isPending ? "در حال ذخیره..." : "ذخیره"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block Confirmation Modal */}
      {blockTarget && (
        <div
          className="admin-overlay"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setBlockTarget(null); overlayMouseDown.current = false; }}
        >
          <div
            className="admin-glass admin-modal"
            style={{ maxWidth: 400 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{ background: blockTarget.status === "banned" ? "var(--admin-success-bg)" : "var(--admin-error-bg)" }}
              >
                {blockTarget.status === "banned" ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--admin-success)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M16 11l2 2 4-4" />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--admin-error)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                    <circle cx="12" cy="12" r="4" />
                    <path d="M9 9l6 6" />
                  </svg>
                )}
              </div>
              <h2 className="text-base font-semibold mb-2" style={{ color: "var(--admin-text-primary)" }}>
                {blockTarget.status === "banned" ? "رفع مسدودی کاربر" : "مسدود کردن کاربر"}
              </h2>
              <p className="text-sm leading-6 mb-1" style={{ color: "var(--admin-text-secondary)" }}>
                {blockTarget.status === "banned"
                  ? "آیا از رفع مسدودی این کاربر اطمینان دارید؟"
                  : "آیا از مسدود کردن این کاربر اطمینان دارید؟"}
              </p>
              <p className="text-xs mb-6 ltr" style={{ color: "var(--admin-text-tertiary)" }}>
                {displayName(blockTarget) !== "—" ? displayName(blockTarget) + " — " : ""}
                {blockTarget.email as string}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setBlockTarget(null)}
                  className="admin-btn admin-btn-secondary flex-1"
                >
                  انصراف
                </button>
                <button
                  onClick={confirmBlock}
                  disabled={updateUser.isPending}
                  className="admin-btn flex-1"
                  style={{
                    background: blockTarget.status === "banned" ? "var(--admin-success)" : "var(--admin-error)",
                    color: "#fff",
                    border: "none",
                  }}
                >
                  {updateUser.isPending
                    ? "در حال انجام..."
                    : blockTarget.status === "banned"
                      ? "رفع مسدودی"
                      : "مسدود کردن"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div
          className="admin-overlay"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setDeleteTarget(null); overlayMouseDown.current = false; }}
        >
          <div
            className="admin-glass admin-modal"
            style={{ maxWidth: 400 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{ background: "var(--admin-error-bg)" }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--admin-error)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                </svg>
              </div>
              <h2 className="text-base font-semibold mb-2" style={{ color: "var(--admin-text-primary)" }}>
                حذف کاربر
              </h2>
              <p className="text-sm leading-6 mb-1" style={{ color: "var(--admin-text-secondary)" }}>
                آیا از حذف این کاربر اطمینان دارید؟ این عملیات قابل بازگشت نیست و اطلاعات ورود و کلیدهای API او غیرفعال می‌شود.
              </p>
              <p className="text-xs mb-6 ltr" style={{ color: "var(--admin-text-tertiary)" }}>
                {displayName(deleteTarget) !== "—" ? displayName(deleteTarget) + " — " : ""}
                {deleteTarget.email as string}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="admin-btn admin-btn-secondary flex-1"
                >
                  انصراف
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteUserMutation.isPending}
                  className="admin-btn flex-1"
                  style={{
                    background: "var(--admin-error)",
                    color: "#fff",
                    border: "none",
                  }}
                >
                  {deleteUserMutation.isPending ? "در حال حذف..." : "حذف"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    {/* User profile drawer */}
      <AdminUserDrawer user={profileUser} onClose={() => setProfileUser(null)} />
    </div>
  );
}
